import { qs, on, lockBodyScroll, unlockBodyScroll } from '../../core/dom';
import { activateFocusTrap, type FocusTrap } from '../../core/focus-trap';
import { ga4 } from '../../core/ga4';
import type { NavModuleInit } from '../../core/types';

const init: NavModuleInit = ({ bus }) => {
  const toggle = qs<HTMLButtonElement>(
    '[data-gn-trigger="slide-out"], [data-testid="slide-out-toggle"]',
  );
  if (!toggle) return;

  let drawer = qs<HTMLElement>('.nav-slideout, #nav-slideout');
  if (!drawer) {
    drawer = document.createElement('section');
    drawer.id = 'nav-slideout';
    drawer.className = 'nav-slideout';
    document.body.appendChild(drawer);
  }
  drawer.setAttribute('role', 'dialog');
  drawer.setAttribute('aria-label', 'Navigation menu');

  let backdrop = qs<HTMLElement>('.gn-slideout__backdrop');
  if (!backdrop) {
    backdrop = document.createElement('div');
    backdrop.className = 'gn-slideout__backdrop';
    backdrop.setAttribute('aria-hidden', 'true');
    document.body.appendChild(backdrop);
  }
  const backdropEl = backdrop;

  let trap: FocusTrap | null = null;

  const setOpen = (open: boolean) => {
    drawer.classList.toggle('is-open', open);
    drawer.classList.toggle('nav-slideout--active', open);
    backdropEl.classList.toggle('is-visible', open);
    toggle.setAttribute('aria-expanded', String(open));
    // Keep aria-hidden in sync — a focused descendant inside an aria-hidden
    // ancestor triggers Chrome's "Blocked aria-hidden" warning.
    if (open) {
      drawer.removeAttribute('aria-hidden');
      drawer.setAttribute('aria-modal', 'true');
    } else {
      drawer.setAttribute('aria-hidden', 'true');
      drawer.removeAttribute('aria-modal');
    }
    if (open) {
      lockBodyScroll();
      trap = activateFocusTrap(drawer);
    } else {
      unlockBodyScroll();
      trap?.destroy();
      trap = null;
    }
    bus.emit('slide-out:toggle', { open });
  };

  const onToggle = () => {
    ga4('controlClicked', { parentClassName: 'Navigation Toggle', text: 'Mobile' });
    setOpen(!drawer.classList.contains('is-open'));
  };
  const onBackdrop = () => setOpen(false);
  const onEscape = (e: KeyboardEvent) => {
    if (e.key === 'Escape' && drawer.classList.contains('is-open')) setOpen(false);
  };

  const offToggle = on(toggle, 'click', onToggle);
  const offBackdrop = on(backdropEl, 'click', onBackdrop);
  const offEscape = on(document, 'keydown', onEscape as (e: Event) => void);
  const offBusUserMenu = bus.on('user-menu:toggle', (d) => {
    if (d?.open && drawer.classList.contains('is-open')) setOpen(false);
  });
  const offBusNotif = bus.on('notifications:open', () => {
    if (drawer.classList.contains('is-open')) setOpen(false);
  });

  return {
    destroy: () => {
      offToggle();
      offBackdrop();
      offEscape();
      offBusUserMenu();
      offBusNotif();
      setOpen(false);
      backdropEl.remove();
    },
  };
};

export default init;
